import { Grid, Tooltip } from "@mui/material";
import React, { FC, useState } from "react";
import GeneralListItem from "@/components/general/generaListItem";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { Collapse, IconButtonProps, styled } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuizIcon from "@mui/icons-material/Quiz";
import styles from "@/components/appointmentDashboard/appointmentTreeItemForms.module.css";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";

interface Props {
  formsToShow: "hidden" | "visible";
  forms: any[];
  appointmentId: any;
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const AppointmentTreeItemForms: FC<Props> = ({
  formsToShow,
  forms,
  appointmentId,
}) => {
  const { t } = useTranslation();
  const [invisibleFormsOpen, setInvisibleFormsOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  const hasIncompleteMandatoryFields = (form: any) => {
    const filledMandatory = form.filled_mandatory_measurements;
    const totalMandatory = form.total_mandatory_measurements;
    return filledMandatory < totalMandatory;
  };

  const renderMandatoryFieldsWarning = (form: any) => {
    const filledMandatory = form.filled_mandatory_measurements;
    const totalMandatory = form.total_mandatory_measurements;

    if (hasIncompleteMandatoryFields(form)) {
      return (
        <Tooltip
          title={`${t("warning")}: ${filledMandatory}/${totalMandatory} ${t(
            "mandatory_fields_filled"
          )}`}
          placement="top"
        >
          <span className={styles.quizIcon}>
            <QuizIcon fontSize="small" />
          </span>
        </Tooltip>
      );
    }
    return null;
  };

  const getWrapperClassName = (form: any) => {
    if (!form.finding_id) {
      return styles.generalListItemWrapperDisabled;
    }
    if (hasIncompleteMandatoryFields(form)) {
      return styles.generalListItemWrapperWarning;
    }
    return styles.generalListItemWrapper;
  };

  const renderItems = () => {
    const examinationSortKey = (form: any) => String(form.examination_id ?? "");
    const sortedFormsByExamination = [...forms].sort((a: any, b: any) =>
      examinationSortKey(a).localeCompare(examinationSortKey(b))
    );

    const filteredVisibleForms = sortedFormsByExamination.filter(
      (form: any) => !form?.form.hidden
    );
    const filteredHiddenForms = sortedFormsByExamination.filter(
      (form: any) => form?.form.hidden
    );

    const groupedVisibleFormsByExamination = filteredVisibleForms.reduce(
      (acc: any, obj: any) => {
        const key = obj.examination_id;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
      },
      {}
    );
    Object.keys(groupedVisibleFormsByExamination).forEach((examination) => {
      groupedVisibleFormsByExamination[examination].sort((a: any, b: any) => {
        if (a.form.widget_id === "anamnese_allgemein") return -1;
        if (b.form.widget_id === "anamnese_allgemein") return 1;
        return 0;
      });
    });

    const groupedHiddenFormsByExamination = filteredHiddenForms.reduce(
      (acc: any, obj: any) => {
        const key = obj.examination_id;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
      },
      {}
    );

    const handleNavigateToForm = (form: any) => {
      navigate(
        "/appointmentDashboard/" +
          appointmentId +
          "/" +
          form.form.id +
          "/" +
          form.finding_id
      );
    };

    const renderlabel = (currentForm: any, label: any) => {
      return (
        <div className={styles.itemWrapper}>
          <h6 className={styles.noMargin}>{currentForm.form.name}</h6>
          <div className={styles.itemContent}>
            {renderMandatoryFieldsWarning(currentForm)}
            {label}

            {currentForm.finding_id ? (
              <Tooltip title={t("open_form")} placement="top-end">
                <span>
                  <ArrowForwardIcon />
                </span>
              </Tooltip>
            ) : (
              <ArrowForwardIcon />
            )}
          </div>
        </div>
      );
    };
    if (formsToShow === "visible") {
      const examinationKeysForVisibleForms = Object.keys(
        groupedVisibleFormsByExamination
      );

      return examinationKeysForVisibleForms.map(
        (examination: any, index: any) => (
          <React.Fragment key={index}>
            {groupedVisibleFormsByExamination[examination].map(
              (currentForm: any) => {
                const label = (
                  <span className={styles.label}>
                    {currentForm.filled_measurements ===
                    currentForm.total_measurements ? (
                      <CheckIcon />
                    ) : (
                      `${currentForm.filled_measurements}/${currentForm.total_measurements}`
                    )}
                  </span>
                );

                return (
                  <GeneralListItem
                    key={currentForm.form.id}
                    dataTestId={`appointment-form-${currentForm.form.id}`}
                    allowHover={true}
                    onClickElement="wrapper"
                    onClick={() => handleNavigateToForm(currentForm)}
                    isDisabled={!currentForm.finding_id}
                    wrapperClassName={getWrapperClassName(currentForm)}
                    hideButton={true}
                  >
                    {renderlabel(currentForm, label)}
                  </GeneralListItem>
                );
              }
            )}
            {index < examinationKeysForVisibleForms.length - 1 && <hr />}
          </React.Fragment>
        )
      );
    }
    if (formsToShow === "hidden" && filteredHiddenForms.length !== 0) {
      const examinationKeysForHiddenForms = Object.keys(
        groupedHiddenFormsByExamination
      );
      return (
        <div>
          <ExpandMore
            expand={invisibleFormsOpen}
            onClick={handleShowInVisibleForms}
            aria-expanded={invisibleFormsOpen}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
          <Collapse
            in={invisibleFormsOpen}
            className={styles.collapseContent}
            timeout="auto"
            unmountOnExit
          >
            {examinationKeysForHiddenForms.map(
              (examination: any, index: any) => (
                <React.Fragment key={index}>
                  {groupedHiddenFormsByExamination[examination].map(
                    (currentForm: any) => {
                      const label = (
                        <span className={styles.label}>
                          {currentForm.filled_measurements ===
                          currentForm.total_measurements ? (
                            <CheckIcon />
                          ) : (
                            `${currentForm.filled_measurements}/${currentForm.total_measurements}`
                          )}
                        </span>
                      );

                      return (
                        <GeneralListItem
                          key={currentForm.form.id}
                          dataTestId={`appointment-form-${currentForm.form.id}`}
                          allowHover={true}
                          onClickElement="wrapper"
                          onClick={() => handleNavigateToForm(currentForm)}
                          isDisabled={!currentForm.finding_id}
                          wrapperClassName={getWrapperClassName(currentForm)}
                          hideButton={true}
                        >
                          {renderlabel(currentForm, label)}
                        </GeneralListItem>
                      );
                    }
                  )}
                </React.Fragment>
              )
            )}
          </Collapse>
        </div>
      );
    }
  };

  const ExpandMore = styled((props: ExpandMoreProps) => {
    // eslint-disable-next-line
    const { expand, children, onClick } = props;
    return (
      <Grid item xs={12} className={styles.expandMoreButton}>
        <span>{t("more_forms")}</span>
        <GenericButton variant="icon" color="iconBase" onClick={onClick}>
          {children}
        </GenericButton>
      </Grid>
    );
  })(({ theme, expand }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const handleShowInVisibleForms = () => {
    setInvisibleFormsOpen(!invisibleFormsOpen);
  };

  return (
    <Grid xs={12} item data-testid="appointment-tree-item-forms">
      <>{renderItems()}</>
    </Grid>
  );
};

export default AppointmentTreeItemForms;
